<script setup lang="ts">
import IconAvatar from '@/core/elements/IconAvatar.vue';
import { EIconAvatarSize } from '@/enums/iconAvatar.enum';
import type { Component } from 'vue';

const props = withDefaults(
  defineProps<{
    title: string;
    description: string;
    icon: Component;
    isSelected: boolean;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const emit = defineEmits<{
  select: [];
}>();

const handleSelect = (): void => {
  if (props.disabled) return;
  emit('select');
};
</script>

<template>
  <div
    class="flex min-h-[126px] w-full items-start gap-lg rounded-group p-lg text-left transition duration-200 focus-visible:outline-focus focus-visible:outline-offset-[3px] max-md:min-h-0 max-md:gap-md max-md:p-md"
    :class="[
      disabled ? 'cursor-default opacity-50' : 'cursor-pointer',
      isSelected ? 'bg-tint/10' : 'bg-surface-secondary md:hover:bg-surface-tertiary',
    ]"
    :aria-checked="isSelected"
    :aria-disabled="disabled || undefined"
    role="radio"
    :tabindex="disabled ? -1 : 0"
    @click="handleSelect"
    @keydown.enter.prevent.stop="handleSelect"
    @keydown.space.prevent.stop="handleSelect"
  >
    <IconAvatar :icon="icon" :size="EIconAvatarSize.LG" />

    <span class="flex min-w-0 flex-1 flex-col text-primary">
      <span class="text-heading font-semibold leading-[170%] tracking-[0.01em]">
        {{ title }}
      </span>
      <span class="text-body leading-[170%] max-md:leading-[155%]">
        {{ description }}
      </span>
    </span>

    <span class="flex shrink-0 items-center py-xs" aria-hidden="true">
      <span
        class="relative size-icon-sm rounded-full"
        :class="isSelected ? 'bg-tint' : 'border border-divider bg-surface-primary'"
      >
        <span
          v-if="isSelected"
          class="absolute left-1/2 top-1/2 size-icon-xxs -translate-x-1/2 -translate-y-1/2 rounded-full bg-white"
        ></span>
      </span>
    </span>
  </div>
</template>
